import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { MessageCircleIcon, MicIcon, SendIcon } from 'lucide-react';
import { useScrollFadeIn } from '../hooks/useScrollFadeIn';
const IMAGE_THEMES = ['Connection', 'Purpose', 'Freedom', 'Growth', 'Expression', 'Belonging', 'Achievement', 'Peace'];
interface Message {
  role: 'bot' | 'user';
  content: string;
}
interface ChatBotProps {
  selectedImages?: number[];
}
export function ChatBot({
  selectedImages = []
}: ChatBotProps) {
  const {
    ref,
    isVisible
  } = useScrollFadeIn();
  const [messages, setMessages] = useState<Message[]>([{
    role: 'bot',
    content: "Welcome! I'm your LiveWright Guide. I just saw your results — they reveal something really interesting about where you are right now. Would you like me to walk you through what stood out?"
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isFirstMessageRef = useRef(true);
  // Generate session ID ONCE on mount
  const sessionIdRef = useRef('lw-demo-' + Math.random().toString(36).substr(2, 9));
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;
    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage
    }]);
    setIsLoading(true);
    // Build context — include exercise results only on the first message
    const context: Record<string, unknown> = {};
    if (isFirstMessageRef.current && selectedImages.length > 0) {
      context.exercise_results = selectedImages.map(idx => IMAGE_THEMES[idx] || 'Unknown');
      isFirstMessageRef.current = false;
    }
    try {
      const response = await fetch('https://spaces.sprinklenet.com/api/bots/69ac7fbea6bf3f0207ddc060/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ks_live_0f0495d52d24ee1ffc135d650cae6fec91ea21d61ef1b01630c94ecaa8f7b1cd'
        },
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          text: userMessage,
          context
        })
      });
      if (!response.ok) throw new Error('API Error');
      const data = await response.json();
      const botReply = data.text.content;
      setMessages(prev => [...prev, {
        role: 'bot',
        content: botReply
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'bot',
        content: "I'm having trouble connecting right now. Please try again in a moment."
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  return <section id="chatbot" className="bg-lightgray py-12 md:py-16 px-4 md:px-8 flex flex-col justify-center" data-id="element-6">
      <motion.div ref={ref} initial={{
      opacity: 0,
      y: 20
    }} animate={isVisible ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 20
    }} transition={{
      duration: 0.6
    }} className="max-w-3xl mx-auto w-full" data-id="element-7">
        <div className="text-center mb-6 md:mb-10" data-id="element-8">
          <h2 className="text-navy text-2xl md:text-4xl font-bold mb-2 md:mb-3" data-id="element-9">
            Meet Your LiveWright Guide
          </h2>
          <p className="text-gray-500" data-id="element-10">
            Ask questions, explore your results, or learn about coaching
            programs
          </p>
        </div>

        <div className="mx-auto w-full md:max-w-[500px] h-[400px] md:h-[500px] rounded-2xl shadow-lg overflow-hidden flex flex-col bg-white border border-gray-200" data-id="element-11">
          {/* Header */}
          <div className="bg-teal text-white p-4 flex items-center gap-3 shadow-sm z-10" data-id="element-12">
            <MessageCircleIcon className="w-6 h-6" data-id="element-13" />
            <span className="font-semibold text-lg" data-id="element-14">LiveWright Guide</span>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 bg-lightgray space-y-4" data-id="element-15">
            {messages.map((msg, idx) => <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-16">
                <div className={`
                    p-3 max-w-[80%] text-sm md:text-base shadow-sm
                    ${msg.role === 'user' ? 'bg-navy text-white rounded-2xl rounded-tr-sm' : 'bg-lightteal text-gray-900 rounded-2xl rounded-tl-sm'}
                  `} data-id="element-17">
                  {msg.content}
                </div>
              </div>)}

            {isLoading && <div className="flex justify-start" data-id="element-18">
                <div className="bg-lightteal p-4 rounded-2xl rounded-tl-sm shadow-sm flex items-center gap-1" data-id="element-19">
                  <span className="typing-dot" data-id="element-20"></span>
                  <span className="typing-dot" data-id="element-21"></span>
                  <span className="typing-dot" data-id="element-22"></span>
                </div>
              </div>}
            <div ref={messagesEndRef} data-id="element-23" />
          </div>

          {/* Input Area */}
          <div className="bg-white p-2 md:p-3 flex items-center gap-1.5 md:gap-2 border-t border-gray-100" data-id="element-24">
            <button className="p-2 rounded-full text-teal hover:bg-lightteal transition-colors shrink-0" aria-label="Voice input" data-id="element-25">
              <MicIcon className="w-5 h-5" data-id="element-26" />
            </button>

            <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder="Type your message..." autoComplete="off" className="flex-1 min-w-0 rounded-full border border-gray-200 px-3 md:px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal/50 text-sm placeholder:text-gray-500" data-id="element-27" />

            <button onClick={handleSend} disabled={!inputValue.trim() || isLoading} className="bg-teal text-white p-2 rounded-full hover:bg-teal/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0" aria-label="Send message" data-id="element-28">
              <SendIcon className="w-5 h-5" data-id="element-29" />
            </button>
          </div>
        </div>
      </motion.div>
    </section>;
}